import React, { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Send, CheckCircle, Loader2, ListChecks } from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL + "/api";
const CAL_URL = "https://cal.com/mindways/discovery-call";

const userTypes = ["Student", "Parent / Guardian"];
const studyLevels = ["Boarding School", "Undergraduate (UG)", "Masters", "MBA", "Other"];
const qualifications = ["Class 10", "Class 12", "Undergraduate degree", "Postgraduate degree", "Other"];
const scoringSystems = ["School Percent", "IB score", "IGCSE grades", "CGPA out of 10", "CGPA out of 4", "Other"];
const programmes = [
  "Business and Management",
  "STEM (Engineering, Computer Science, Sciences)",
  "Economics, Finance and Accounting",
  "Social Sciences (Psychology and related)",
  "Arts and Humanities",
  "Design and Architecture",
  "Law",
  "Medicine and Health Sciences",
  "Data, AI and Analytics",
  "Public Health and Policy",
  "Other",
];
const destinations = [
  "Undecided",
  "United Kingdom", "United States", "Canada", "Germany", "France",
  "Ireland", "Dubai / UAE", "Singapore", "Hong Kong", "Japan", "South Korea",
  "Australia", "New Zealand", "India", "Europe", "Other",
];
const examOptions = [
  "IELTS / TOEFL / PTE / Duolingo",
  "SAT / ACT",
  "GRE / GMAT",
  "None yet",
];
const intakes = [
  "Sep 2026", "Jan 2027", "Sep 2027",
  "Jan 2028", "Sep 2028", "Jan 2029", "Sep 2029",
];

const labelStyle = {
  display: "block",
  fontSize: "13px",
  fontWeight: "600",
  color: "#374151",
  fontFamily: "'DM Sans', sans-serif",
  marginBottom: "6px",
};

const inputStyle = {
  width: "100%",
  padding: "11px 14px",
  border: "1px solid #E2E8F0",
  borderRadius: "6px",
  fontSize: "14px",
  fontFamily: "'DM Sans', sans-serif",
  color: "#0F172A",
  boxSizing: "border-box",
  transition: "border-color 0.2s",
  outline: "none",
  backgroundColor: "#fff",
};

function getUTMParams() {
  const params = new URLSearchParams(window.location.search);
  return {
    utm_source: params.get("utm_source") || "",
    utm_medium: params.get("utm_medium") || "",
    utm_campaign: params.get("utm_campaign") || "",
  };
}

export default function GetShortlist() {
  const [form, setForm] = useState({
    name: "", user_type: "", study_level: "",
    last_qualification: "", scoring_system: "", score: "",
    preferred_programme: "", destination: "",
    exams_taken: [], start_intake: "",
    whatsapp: "", email: "", message: "",
  });
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState("");

  const update = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const toggleExam = (exam) => {
    setForm((f) => ({
      ...f,
      exams_taken: f.exams_taken.includes(exam)
        ? f.exams_taken.filter((e) => e !== exam)
        : [...f.exams_taken, exam],
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");
    try {
      const utm = getUTMParams();
      const res = await fetch(`${API}/leads`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ ...form, lead_type: "shortlist", source: "get-shortlist-section", ...utm }),
      });
      const data = await res.json();
      if (res.ok && data.success) setSuccess(true);
      else setError(data.detail || "Something went wrong. Please try again.");
    } catch {
      setError("Unable to submit. Please try again or WhatsApp us directly.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <section
      id="get-shortlist"
      data-testid="get-shortlist-section"
      style={{ padding: "100px 0", backgroundColor: "#fff" }}
    >
      <div style={{ maxWidth: "1280px", margin: "0 auto", padding: "0 24px" }}>
        <div className="mw-grid-shortlist">

          {/* Left: copy */}
          <motion.div
            initial={{ opacity: 0, y: 24 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            style={{ position: "sticky", top: "100px" }}
          >
            <span style={{ fontSize: "13px", fontWeight: "700", letterSpacing: "0.12em", textTransform: "uppercase", color: "#F5A000", fontFamily: "'DM Sans', sans-serif" }}>
              No Commitment Required
            </span>
            <h2 style={{
              fontFamily: "'Playfair Display', serif",
              fontSize: "clamp(26px, 2.5vw, 36px)",
              fontWeight: "700",
              color: "#0F172A",
              margin: "14px 0 18px",
              lineHeight: "1.25",
            }}>
              Get Your University Shortlist
            </h2>
            <p style={{ fontSize: "17px", lineHeight: "1.7", color: "#64748B", fontFamily: "'DM Sans', sans-serif", margin: "0 0 32px" }}>
              Get clarity on fit, outcomes, scholarship potential, and total cost. Then decide.
            </p>

            <div style={{ display: "flex", flexDirection: "column", gap: "14px" }}>
              {[
                { icon: ListChecks, text: "Curated list of universities matched to your profile and goals" },
                { icon: CheckCircle, text: "Safe, match, and ambitious mix across your target destinations" },
                { icon: CheckCircle, text: "Scholarship potential and total cost assessment" },
                { icon: CheckCircle, text: "Follow-up from our team within 1 business day" },
              ].map((item, i) => {
                const Icon = item.icon;
                return (
                  <div key={i} style={{ display: "flex", gap: "12px", alignItems: "flex-start" }}>
                    <div style={{ width: "34px", height: "34px", borderRadius: "8px", backgroundColor: "#EEF0FC", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
                      <Icon size={15} style={{ color: "#2B2F9E" }} />
                    </div>
                    <p style={{ fontSize: "15px", color: "#475569", fontFamily: "'DM Sans', sans-serif", margin: "8px 0 0", lineHeight: "1.55" }}>
                      {item.text}
                    </p>
                  </div>
                );
              })}
            </div>

            <div style={{ marginTop: "32px", padding: "18px 20px", backgroundColor: "#F8FAFC", borderRadius: "10px", borderLeft: "3px solid #F5A000" }}>
              <p style={{ fontFamily: "'DM Sans', sans-serif", fontSize: "14px", color: "#64748B", margin: 0, lineHeight: "1.6" }}>
                Prefer to talk directly?{" "}
                <a href={`https://wa.me/919821916960`} target="_blank" rel="noopener noreferrer" style={{ color: "#2B2F9E", fontWeight: "700", textDecoration: "none" }}>
                  WhatsApp us
                </a>{" "}or{" "}
                <a href={CAL_URL} target="_blank" rel="noopener noreferrer" style={{ color: "#2B2F9E", fontWeight: "700", textDecoration: "none" }}>
                  book a strategy call
                </a>.
              </p>
            </div>
          </motion.div>

          {/* Right: form */}
          <motion.div
            initial={{ opacity: 0, y: 24 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ delay: 0.15 }}
          >
            <div style={{ backgroundColor: "#F8FAFC", borderRadius: "16px", padding: "40px", border: "1px solid #E2E8F0" }}>
              <AnimatePresence mode="wait">
                {success ? (
                  <motion.div
                    key="success"
                    initial={{ opacity: 0, scale: 0.95 }}
                    animate={{ opacity: 1, scale: 1 }}
                    data-testid="shortlist-success-message"
                    style={{ textAlign: "center", padding: "32px 0" }}
                  >
                    <div style={{ width: "72px", height: "72px", borderRadius: "50%", backgroundColor: "#F0FDF4", display: "flex", alignItems: "center", justifyContent: "center", margin: "0 auto 24px" }}>
                      <CheckCircle size={36} style={{ color: "#10B981" }} />
                    </div>
                    <h3 style={{ fontFamily: "'Playfair Display', serif", fontSize: "24px", fontWeight: "700", color: "#0F172A", margin: "0 0 12px" }}>
                      Request Received
                    </h3>
                    <p style={{ fontFamily: "'DM Sans', sans-serif", fontSize: "16px", color: "#64748B", margin: 0, lineHeight: "1.65" }}>
                      Thanks. Our team will contact you within 1 business day with your preliminary shortlist.
                    </p>
                  </motion.div>
                ) : (
                  <motion.form
                    key="form"
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    onSubmit={handleSubmit}
                    data-testid="shortlist-form"
                    style={{ display: "flex", flexDirection: "column", gap: "16px" }}
                  >
                    {/* About you */}
                    <div style={{ borderBottom: "1px solid #E2E8F0", paddingBottom: "4px", marginBottom: "4px" }}>
                      <span style={{ fontSize: "11px", fontWeight: "700", letterSpacing: "0.1em", textTransform: "uppercase", color: "#94A3B8", fontFamily: "'DM Sans', sans-serif" }}>About You</span>
                    </div>

                    <div>
                      <label style={labelStyle}>Full Name *</label>
                      <input data-testid="shortlist-name-input" required type="text" placeholder="Your full name" value={form.name} onChange={(e) => update("name", e.target.value)} style={inputStyle} onFocus={(e) => { e.target.style.borderColor = "#2B2F9E"; }} onBlur={(e) => { e.target.style.borderColor = "#E2E8F0"; }} />
                    </div>

                    <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "12px" }}>
                      <div>
                        <label style={labelStyle}>Are you the student or parent? *</label>
                        <select data-testid="shortlist-user-type-select" required value={form.user_type} onChange={(e) => update("user_type", e.target.value)} style={inputStyle}>
                          <option value="">Select</option>
                          {userTypes.map((s) => <option key={s} value={s}>{s}</option>)}
                        </select>
                      </div>
                      <div>
                        <label style={labelStyle}>Study level *</label>
                        <select data-testid="shortlist-stage-select" required value={form.study_level} onChange={(e) => update("study_level", e.target.value)} style={inputStyle}>
                          <option value="">Select level</option>
                          {studyLevels.map((s) => <option key={s} value={s}>{s}</option>)}
                        </select>
                      </div>
                    </div>

                    <div>
                      <label style={labelStyle}>Last completed qualification *</label>
                      <select data-testid="shortlist-qualification-select" required value={form.last_qualification} onChange={(e) => update("last_qualification", e.target.value)} style={inputStyle}>
                        <option value="">Select qualification</option>
                        {qualifications.map((q) => <option key={q} value={q}>{q}</option>)}
                      </select>
                    </div>

                    <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "12px" }}>
                      <div>
                        <label style={labelStyle}>Scoring system</label>
                        <select data-testid="shortlist-scoring-select" value={form.scoring_system} onChange={(e) => update("scoring_system", e.target.value)} style={inputStyle}>
                          <option value="">Select system</option>
                          {scoringSystems.map((s) => <option key={s} value={s}>{s}</option>)}
                        </select>
                      </div>
                      <div>
                        <label style={labelStyle}>Score (e.g. 85, 38, 7.8)</label>
                        <input data-testid="shortlist-score-input" type="text" placeholder="Enter your score" value={form.score} onChange={(e) => update("score", e.target.value)} style={inputStyle} onFocus={(e) => { e.target.style.borderColor = "#2B2F9E"; }} onBlur={(e) => { e.target.style.borderColor = "#E2E8F0"; }} />
                      </div>
                    </div>

                    {/* Goals */}
                    <div style={{ borderBottom: "1px solid #E2E8F0", paddingBottom: "4px", marginBottom: "4px", marginTop: "4px" }}>
                      <span style={{ fontSize: "11px", fontWeight: "700", letterSpacing: "0.1em", textTransform: "uppercase", color: "#94A3B8", fontFamily: "'DM Sans', sans-serif" }}>Your Goals</span>
                    </div>

                    <div>
                      <label style={labelStyle}>Preferred programme *</label>
                      <select data-testid="shortlist-programme-select" required value={form.preferred_programme} onChange={(e) => update("preferred_programme", e.target.value)} style={inputStyle}>
                        <option value="">Select programme area</option>
                        {programmes.map((p) => <option key={p} value={p}>{p}</option>)}
                      </select>
                    </div>

                    <div>
                      <label style={labelStyle}>Preferred destination *</label>
                      <select data-testid="shortlist-destination-select" required value={form.destination} onChange={(e) => update("destination", e.target.value)} style={inputStyle}>
                        <option value="">Select destination</option>
                        {destinations.map((d) => <option key={d} value={d}>{d}</option>)}
                      </select>
                    </div>

                    {/* Tests */}
                    <div style={{ borderBottom: "1px solid #E2E8F0", paddingBottom: "4px", marginBottom: "4px", marginTop: "4px" }}>
                      <span style={{ fontSize: "11px", fontWeight: "700", letterSpacing: "0.1em", textTransform: "uppercase", color: "#94A3B8", fontFamily: "'DM Sans', sans-serif" }}>Test Preparation</span>
                    </div>

                    <div>
                      <label style={labelStyle}>Have you taken any of these exams?</label>
                      <div style={{ display: "flex", flexWrap: "wrap", gap: "8px" }}>
                        {examOptions.map((exam) => {
                          const selected = form.exams_taken.includes(exam);
                          return (
                            <button
                              key={exam}
                              type="button"
                              data-testid={`exam-chip-${exam.replace(/[\s/]+/g, "-").toLowerCase()}`}
                              onClick={() => toggleExam(exam)}
                              style={{
                                padding: "7px 14px",
                                borderRadius: "20px",
                                border: `1.5px solid ${selected ? "#2B2F9E" : "#E2E8F0"}`,
                                backgroundColor: selected ? "#EEF0FC" : "#fff",
                                color: selected ? "#2B2F9E" : "#64748B",
                                fontSize: "13px",
                                fontWeight: selected ? "700" : "500",
                                fontFamily: "'DM Sans', sans-serif",
                                cursor: "pointer",
                                transition: "all 0.15s",
                              }}
                            >
                              {exam}
                            </button>
                          );
                        })}
                      </div>
                    </div>

                    {/* Timeline */}
                    <div style={{ borderBottom: "1px solid #E2E8F0", paddingBottom: "4px", marginBottom: "4px", marginTop: "4px" }}>
                      <span style={{ fontSize: "11px", fontWeight: "700", letterSpacing: "0.1em", textTransform: "uppercase", color: "#94A3B8", fontFamily: "'DM Sans', sans-serif" }}>Timeline</span>
                    </div>

                    <div>
                      <label style={labelStyle}>When do you want to start studies? *</label>
                      <select data-testid="shortlist-intake-select" required value={form.start_intake} onChange={(e) => update("start_intake", e.target.value)} style={inputStyle}>
                        <option value="">Select intake</option>
                        {intakes.map((i) => <option key={i} value={i}>{i}</option>)}
                      </select>
                      <p style={{ fontSize: "12px", color: "#94A3B8", fontFamily: "'DM Sans', sans-serif", margin: "4px 0 0" }}>Sep = Fall intake · Jan = Spring / Winter intake</p>
                    </div>

                    {/* Contact */}
                    <div style={{ borderBottom: "1px solid #E2E8F0", paddingBottom: "4px", marginBottom: "4px", marginTop: "4px" }}>
                      <span style={{ fontSize: "11px", fontWeight: "700", letterSpacing: "0.1em", textTransform: "uppercase", color: "#94A3B8", fontFamily: "'DM Sans', sans-serif" }}>Contact</span>
                    </div>

                    <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "12px" }}>
                      <div>
                        <label style={labelStyle}>WhatsApp Number *</label>
                        <input data-testid="shortlist-whatsapp-input" required type="tel" placeholder="+91 XXXXX XXXXX" value={form.whatsapp} onChange={(e) => update("whatsapp", e.target.value)} style={inputStyle} onFocus={(e) => { e.target.style.borderColor = "#2B2F9E"; }} onBlur={(e) => { e.target.style.borderColor = "#E2E8F0"; }} />
                      </div>
                      <div>
                        <label style={labelStyle}>Email Address *</label>
                        <input data-testid="shortlist-email-input" required type="email" placeholder="you@example.com" value={form.email} onChange={(e) => update("email", e.target.value)} style={inputStyle} onFocus={(e) => { e.target.style.borderColor = "#2B2F9E"; }} onBlur={(e) => { e.target.style.borderColor = "#E2E8F0"; }} />
                      </div>
                    </div>

                    <div>
                      <label style={labelStyle}>Anything specific we should know? (optional)</label>
                      <textarea data-testid="shortlist-message-textarea" placeholder="Subjects, budget, career goals, concerns..." value={form.message} onChange={(e) => update("message", e.target.value)} rows={3} style={{ ...inputStyle, resize: "vertical", minHeight: "80px" }} onFocus={(e) => { e.target.style.borderColor = "#2B2F9E"; }} onBlur={(e) => { e.target.style.borderColor = "#E2E8F0"; }} />
                    </div>

                    {error && (
                      <p data-testid="shortlist-error-message" style={{ color: "#EF4444", fontSize: "14px", fontFamily: "'DM Sans', sans-serif", margin: 0 }}>{error}</p>
                    )}

                    <button
                      data-testid="shortlist-submit-btn"
                      type="submit"
                      disabled={loading}
                      style={{
                        backgroundColor: loading ? "#94A3B8" : "#F5A000",
                        color: "#fff",
                        border: "none",
                        padding: "16px",
                        borderRadius: "6px",
                        fontSize: "16px",
                        fontWeight: "700",
                        cursor: loading ? "not-allowed" : "pointer",
                        fontFamily: "'DM Sans', sans-serif",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        gap: "8px",
                        boxShadow: "0 8px 24px rgba(245,160,0,0.25)",
                        transition: "all 0.2s",
                        marginTop: "4px",
                      }}
                    >
                      {loading ? <><Loader2 size={18} style={{ animation: "spin 1s linear infinite" }} /> Submitting…</> : <><Send size={18} /> Get My Shortlist</>}
                    </button>
                  </motion.form>
                )}
              </AnimatePresence>
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
}
